import { memo } from 'react';
import { Check } from 'lucide-react';
import ChoixRenderer from '../ChoixRenderer';

const LETTRES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

// Grille adaptative selon le nombre de choix.
// Les choix avec objets (image, expression…) sont compactés via le prop `compact`
// sur ChoixRenderer, donc ils peuvent suivre la même grille que les textes.
// L'élève peut toujours zoomer via ZoomableMedia.
const getGridClass = (count) => {
    if (count <= 4) return 'grid-cols-2';
    if (count <= 6) return 'grid-cols-3';
    return 'grid-cols-4';
};

/**
 * Rendu preview/élève d'une question QCM.
 * Affiche les choix (2 à 8) avec la bonne réponse mise en valeur.
 *
 * @param {Array} choix - Les choix (texte ou objet élément), 2 à 8
 * @param {string|Array} bonneReponse - La lettre ("A") ou les lettres (["A","C"]) des bonnes réponses
 * @param {boolean} multi - Si true, QCM multi-réponses
 */
const QCMRenderer = memo(function QCMRenderer({ choix, bonneReponse, multi, light }) {
    if (!choix || choix.length === 0) return null;

    // Construire un Set des index corrects (supporte string et array)
    const bonnesIndexes = new Set(
        (Array.isArray(bonneReponse) ? bonneReponse : [bonneReponse])
            .map(l => LETTRES.indexOf(l))
            .filter(i => i >= 0)
    );

    return (
        <div>
            {!light && (
                <span className={`text-[10px] font-semibold uppercase tracking-wider mb-2 block text-slate-400`}>
                    {multi ? 'Réponses (plusieurs possibles)' : 'Réponses'}
                </span>
            )}
            <div className={`grid gap-2 ${getGridClass(choix.length)}`}>
                {choix.map((c, i) => {
                    const isCorrect = bonnesIndexes.has(i);
                    return (
                        <div
                            key={i}
                            className={`flex items-start gap-2.5 px-3 py-2.5 rounded-xl border transition-all ${
                                isCorrect
                                    ? light ? 'border-emerald-300 bg-emerald-50' : 'border-emerald-500/50 bg-emerald-500/10'
                                    : light ? 'border-sky-200 bg-white' : 'border-slate-600 bg-slate-700/50'
                            }`}
                        >
                            <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-extrabold flex-shrink-0 mt-0.5 ${
                                isCorrect
                                    ? 'bg-emerald-500 text-white'
                                    : light ? 'bg-sky-100 text-sky-700' : 'bg-slate-600 text-slate-300'
                            }`}>
                                {isCorrect ? <Check size={14} strokeWidth={3} /> : LETTRES[i]}
                            </span>
                            <div className="flex-1 min-w-0">
                                <ChoixRenderer
                                    choix={c}
                                    className={`text-sm ${
                                        isCorrect
                                            ? light ? 'text-emerald-700 font-medium' : 'text-emerald-200 font-medium'
                                            : light ? 'text-sky-800' : 'text-slate-200'
                                    }`}
                                    light={light}
                                    compact
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
});

export default QCMRenderer;
